import { SectionHeader } from "@src/components/general/SectionHeader";
import { Text } from "@src/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/components/layout/Flexbox";
import { Badge } from "@src/components/ui/badge";
import { Card, CardContent, CardHeader } from "@src/components/ui/card";
import { AlertCircle, Copy, Sparkles, type LucideIcon } from "lucide-react";
import { toast } from "sonner";

export type MedicalCodeRow = {
  code: string;
  description: string | null;
  creationType: "ai" | "template" | null;
};

interface CodingCardProps {
  title: string;
  icon: LucideIcon;
  /**
   * When `undefined`, the card renders its original static placeholder
   * (used by the Q&VB card, which we don't have data for yet).
   * When defined, the card renders one of: error, loading skeleton,
   * empty state, or the populated list.
   */
  rows?: MedicalCodeRow[];
  isLoading?: boolean;
  isError?: boolean;
}

export const CodingCard = ({
  title,
  icon: Icon,
  rows,
  isLoading = false,
  isError = false,
}: CodingCardProps) => {
  return (
    <Card className="flex-1 border-border shadow-soft">
      <CardHeader className="pb-3">
        <HorizontalFlexbox alignItems="center" justifyContent="space-between">
          <HorizontalFlexbox alignItems="center" gap="xs">
            <Icon className="size-4 text-accent" />
            <SectionHeader>{title}</SectionHeader>
          </HorizontalFlexbox>
          <button
            type="button"
            aria-label="Copy text"
            disabled={!rows?.length}
            onClick={() => copyRows(rows)}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
          >
            <Copy className="size-3.5" />
          </button>
        </HorizontalFlexbox>
      </CardHeader>
      <CardContent className="pt-0">
        <VerticalFlexbox gap="sm">
          {renderBody({ rows, isLoading, isError })}
        </VerticalFlexbox>
      </CardContent>
    </Card>
  );
};

function renderBody({
  rows,
  isLoading,
  isError,
}: {
  rows: MedicalCodeRow[] | undefined;
  isLoading: boolean;
  isError: boolean;
}) {
  if (rows === undefined) {
    return (
      <Text sizing="sm" appearance="subdued" className="italic">
        Awaiting AI suggestions...
      </Text>
    );
  }

  if (isError) {
    return (
      <HorizontalFlexbox
        alignItems="center"
        gap="xs"
        className="text-destructive"
      >
        <AlertCircle className="size-4 shrink-0" />
        <Text sizing="sm" className="italic text-destructive">
          Couldn't load coding suggestions.
        </Text>
      </HorizontalFlexbox>
    );
  }

  if (isLoading && rows.length === 0) {
    return (
      <>
        <SkeletonRow />
        <SkeletonRow />
        <SkeletonRow />
      </>
    );
  }

  if (rows.length === 0) {
    return (
      <Text sizing="sm" appearance="subdued" className="italic">
        No codes detected yet.
      </Text>
    );
  }

  return (
    <>
      {rows.map((row) => (
        <Row key={row.code} row={row} />
      ))}
    </>
  );
}

function Row({ row }: { row: MedicalCodeRow }) {
  return (
    <HorizontalFlexbox
      alignItems="center"
      justifyContent="space-between"
      gap="sm"
      className="py-1"
    >
      <HorizontalFlexbox alignItems="baseline" gap="sm" className="min-w-0">
        <span className="font-mono text-sm">{row.code}</span>
        {row.description && (
          <span
            className="truncate text-sm text-muted-foreground"
            title={row.description}
          >
            {row.description}
          </span>
        )}
      </HorizontalFlexbox>
      {row.creationType === "ai" && (
        <Badge
          variant="outline"
          className="shrink-0 gap-1 border-success/30 bg-success/10 text-success"
        >
          <Sparkles className="size-3" />
          AI-suggested
        </Badge>
      )}
    </HorizontalFlexbox>
  );
}

function SkeletonRow() {
  return (
    <div
      data-testid="coding-skeleton-row"
      className="my-1 flex items-center gap-2"
    >
      <div className="h-4 w-12 animate-pulse rounded bg-muted" />
      <div className="h-4 w-40 animate-pulse rounded bg-muted" />
    </div>
  );
}

async function copyRows(rows: MedicalCodeRow[] | undefined) {
  if (!rows?.length) return;
  const lines = rows.map((r) =>
    r.description ? `${r.code}\t${r.description}` : r.code,
  );
  try {
    await navigator.clipboard.writeText(lines.join("\n"));
    toast.success(`Copied ${rows.length} code${rows.length === 1 ? "" : "s"}`);
  } catch (err) {
    console.error("[CodingCard] clipboard write failed", err);
    toast.error("Couldn't copy — clipboard unavailable");
  }
}
